import { obtenerRuta } from './common';
import { CENTER_VALENCIA } from './constants/constants';

let iconSelected;
/**
 * Crea un mapa leaflet con los parametros introducidos
 * @param {*} mapIdDomElement Id del elemento del DOM que contendra el mapa
 * @param {*} objIndex Objeto con los indices addres, id, location.
 * @param {*} resultset Array de datos que contiene la informacion de los marcadores.
 * @param {*} objMarker Objeto con los estilos seleccionado y deseleccionado del marcador.
 * @param {*} idDefaultMarker Identificador del marcador seleccionado inicialmente en el mapa.
 * @param {*} handlerClick Función que se ejecutará cuando se realice una pulsación en un marcador del mapa.
 * @param {*} optionsMap Opciones de configuracion zoom del mapa.
 * @param {*} showHover Booleano que indica si se muestra un hover.
 */
export function createLeafletMapPolygon(
  mapIdDomElement,
  objIndex,
  resultset,
  objMarker,
  idDefaultMarker,
  handlerClick,
  optionsMap,
  showHover = false,
) {
  const datosGeoJson = obtenerGeoJson(
    resultset,
    objIndex.id,
    objIndex.addres,
    objIndex.location,
  );
  // Definición del Mapa
  const myMap = L.map(mapIdDomElement).setView(
    CENTER_VALENCIA,
    optionsMap ? optionsMap.initialZoom : 13,
  );
  const cartoDBPositron = L.tileLayer(
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    },
  );

  // Se Definen los niveles Max y Min de Zoom en el Mapa
  myMap.setMinZoom(optionsMap ? optionsMap.minZoom : 5);
  myMap.setMaxZoom(optionsMap ? optionsMap.maxZoom : 20);

  // Se Carga el Mapa
  cartoDBPositron.addTo(myMap);

  drawMarkersPolygon(
    myMap,
    datosGeoJson,
    resultset,
    objIndex,
    showHover,
    objMarker,
    idDefaultMarker,
    handlerClick,
  );
}

/**
 * Crea un mapa leaflet con los parametros introducidos
 * @param {*} mapIdDomElement Id del elemento del DOM que contendra el mapa
 * @param {*} minZoom Zoom minimo del mapa
 * @param {*} maxZoom Zoom maximo del mapa
 * @param {*} initialZoom Zoom inicial con el que cargara el mapa
 * @param {*} datos Array de datos que contiene la informacion de los marcadores. Previamente se puede formatear un resultset con el metodo obtenerGeoJson().
 * @param {*} iconPath Ruta del icono del marcador.
 * @param {*} selectedIconPath Ruta del icono del marcador seleccionado
 * @param {*} pentahoParameterId Id del parametro de Pentaho que cambiara de valor cuando se haga click en un marcador.
 */
export function createLeafletMap(
  mapIdDomElement = 'myMap',
  minZoom = 5,
  maxZoom = 20,
  initialZoom = 13,
  datos,
  iconPath,
  selectedIconPath,
  pentahoParameterId,
) {
  // Definición del Mapa
  const myMap = L.map(mapIdDomElement).setView(
    [39.46975, -0.37739],
    initialZoom,
  );
  const cartoDBPositron = L.tileLayer(
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    },
  );

  // Se Definen los niveles Max y Min de Zoom en el Mapa
  myMap.setMinZoom(minZoom);
  myMap.setMaxZoom(maxZoom);

  // Se Carga el Mapa
  cartoDBPositron.addTo(myMap);

  const markerElements = createObjectMarkerElements();
  drawMarkers(
    myMap,
    datos,
    iconPath,
    selectedIconPath,
    markerElements,
    pentahoParameterId,
  );
}

/**
 * Devuelve un objeto que permite gestionar una lista de ID´s contenida en un Array.
 * Lo utilizamos para almacenar los ID´s de los marcadores que se mostraran en el mapa.
 * @returns Objeto
 */
export function createObjectMarkerElements() {
  return {
    // Lista que contendra los ID´s de los elementos del mapa.
    listOfselectedElements: [],
    elementExist(element) {
      if (this.listOfselectedElements.filter((x) => x === element).length > 0) {
        return true;
      }
      return false;
    },
    deleteElementOfList(element) {
      const index = this.listOfselectedElements.indexOf(element);
      if (index > -1) {
        this.listOfselectedElements.splice(index, 1);
      }
    },
  };
}

/**
 * Devuelve las location en formato geojson para mostrarlos en un mapa leaflet
 * @param {*} locationData Array que contiene el location,id y dirección.
 * @param {*} positionId Posicion del array que indica la id.
 * @param {*} positionAdress Posicion del array que indica la dirección.
 * @param {*} positionLocation Posicion del array que indica la location.
 * @returns localizaciones en formato geoJson
 */

export function obtenerGeoJson(
  locationData,
  positionId = 0,
  positionAdress = 1,
  positionLocation = 2,
) {
  const { Buffer } = require('buffer');
  const wkx = require('wkx');
  const geoJSON = {
    type: 'FeatureCollection',
    features: [],
  };

  // Eliminamos valores duplicados
  const arrayResultadoPosicion = [];
  const arrayCompararPosicion = new Set();
  locationData.forEach((item) => {
    const valor = item[positionLocation];
    if (!arrayCompararPosicion.has(valor)) {
      arrayCompararPosicion.add(valor);
      arrayResultadoPosicion.push(item);
    }
  });

  // Creamos el GeoJson con parametros geometry y properties
  arrayResultadoPosicion.forEach((item) => {
    const geom = wkx.Geometry.parse(Buffer.from(item[positionLocation], 'hex'));

    const feature = {
      type: 'Feature',
      properties: {
        objectoId: item[positionId],
        address: item[positionAdress],
      },

      geometry: geom.toGeoJSON(),
    };
    geoJSON.features.push(feature);
  });
  return geoJSON;
}

/**
 * Pinta en un mapa los marcadores indicados
 * @param {*} myMap Mapa en el que se pintaran los marcadores.
 * @param {*} datos Array de datos que contiene la informacion de los marcadores. Previamente se puede formatear un resultset con el metodo obtenerGeoJson().
 * @param {*} iconPath Ruta del icono del marcador.
 * @param {*} selectedIconPath Ruta del icono del marcador seleccionado
 * @param {*} markerElements Pasar un objeto creado con createObjectMarkerElements().
 * @param {*} pentahoParameterId Id del parametro de Pentaho que cambiara de valor cuando se haga click en un marcador.
 */
export function drawMarkers(
  myMap,
  datos,
  iconPath,
  selectedIconPath,
  markerElements,
  pentahoParameterId,
) {
  // Creación de los marcadores que se va a mostrar en el mapa
  function getIcon(url) {
    return {
      iconUrl: obtenerRuta(url),
      iconSize: [34, 34], // size of the icon
      shadowSize: [50, 64], // size of the shadow
      iconAnchor: [17, 17], // point of the icon which will correspond to marker's location
      shadowAnchor: [4, 62], // the same for the shadow
      popupAnchor: [-3, -76], // point from which the popup should open relative to the iconAnchor
    };
  }
  const imgIcon = L.icon(getIcon(iconPath));
  const imgIconSelected = L.icon(getIcon(selectedIconPath));

  // Que se muestra en el popup cuando hacemos click en un punto de medida
  function popup(feature, layer) {
    if (feature.properties && feature.properties.address) {
      layer.on('click', () => {
        if (markerElements.elementExist(feature.properties.objectoId)) {
          markerElements.deleteElementOfList(feature.properties.objectoId);
        } else {
          markerElements.listOfselectedElements.push(
            feature.properties.objectoId,
          );
        }
        if (pentahoParameterId) {
          Dashboards.fireChange(
            pentahoParameterId,
            markerElements.listOfselectedElements,
          );
        }
        drawMarkers(
          myMap,
          datos,
          iconPath,
          selectedIconPath,
          markerElements,
          pentahoParameterId,
        );
      });
    }
  }

  L.geoJSON(datos, {
    onEachFeature: popup,
    pointToLayer(feature, latlng) {
      let imgIconOutput = imgIcon;
      if (markerElements.elementExist(feature.properties.objectoId)) {
        imgIconOutput = imgIconSelected;
      }
      return L.marker(latlng, { icon: imgIconOutput });
    },
  }).addTo(myMap);
}

/**
 * Mostrar informacion al realizar click sobre una entidad / marcador del mapa
 * @param {Array} datos Array de objetos que contiene la informacion del mapa
 * @param {Integer} indexAddres Indice que indica el dato a mostrar en el PopUp
 * @param {Object} feature Entidad geografica del mapa
 * @param {Object} layer Representacion grafica del feature
 * @param {Function} handlerClick Funcion que se ejecutará al hacer click sobre una entidad
 */
function popup(datos, objIndex, feature, layer, handlerClick) {
  const direccion = feature.properties.address;
  const id = feature.properties.objectoId;

  if (feature.properties && direccion) {
    layer.on('click', () => {
      const dataAddres = datos.filter((element) => element[objIndex.id] === id);
      handlerClick(dataAddres, direccion);
    });
  }
}
/**
 * Eventos que modifican los elementos del mapa
 * @param {Object} feature Entidad geografica del mapa
 * @param {Object} layer Representacion grafica del feature
 * @param {Boolean} showHover Indica si se mostrara información al pasar
 * el ratón por encima de una entidad
 * @param {Object} stylePolygon Estilo de una entidad no seleccionada
 * @param {Object} stylePolygonSelected Estilo de una entidad seleccionada
 */
function handlerEvents(feature, layer, showHover, objMarker) {
  const direccion = feature.properties.address;
  if (showHover) {
    layer.on('mouseover', () => {
      layer.bindPopup(direccion).openPopup();
    });
    layer.on('mouseout', () => {
      layer.closePopup();
    });
  }
  layer.on('click', () => {
    if (iconSelected) {
      iconSelected.setStyle(objMarker.unselected);
      layer.setStyle(objMarker.selected);
    } else {
      layer.setStyle(objMarker.selected);
    }
    iconSelected = layer;
  });
}
/**
 *  Dibujar entidades/markers en el mapa
 * @param {Object} myMap Instancia mapa Leaflet
 * @param {geoJSON} datos Localizaciones en formato GeoJSON
 * @param {Array} resultset Informacion de las entidades a dibujar en el mapa
 * @param {Integer} objIndex Objeto con los indices de informacion del mapa (id, addres)
 * @param {Boolean} showHover Indica si se mostrara PopUp
 * @param {Object} objMarker Objeto con los estilos de una entidad
 * @param {String} idDefaultMarker Identificador de la entidad que estara seleccionada inicialmente
 * @param {Function} handlerClick Funcion que se ejecutará al hacer click sobre una entidad
 */
export function drawMarkersPolygon(
  myMap,
  datos,
  resultset,
  objIndex,
  showHover,
  objMarker,
  idDefaultMarker,
  handlerClick,
) {
  L.geoJSON(datos, {
    onEachFeature(feature, layer) {
      if (feature.properties.address === idDefaultMarker) {
        iconSelected = layer;
      }

      popup(resultset, objIndex, feature, layer, handlerClick);
      handlerEvents(feature, layer, showHover, objMarker);
    },
    style: (feature) =>
      feature.properties.address === idDefaultMarker
        ? objMarker.selected
        : objMarker.unselected,
  }).addTo(myMap);
}
